/**
 * Pure derivations over the validation payload.
 *
 * Everything the console computes lives here so it can be unit-tested without
 * rendering, and so no component can quietly invent its own version of a rule
 * that matters — above all the one about never merging the two chains.
 *
 * NOTHING here recomputes a backend number. Scores, counts and headlines are
 * rendered exactly as supplied; these functions only sort, group, filter and
 * shape what the payload already contains.
 */

import type {
  IssueSeverity,
  ValidationChain,
  ValidationIssue,
  ValidationPeriod,
  ValidationStatement,
  ValidationStatus,
  ValidationSummary,
} from '../../../types/dataQuality';
import { formatPeriodRange, formatQualityScore, resolveScore } from '../utils/dataQualityFormat';
import { ruleFamily, type RuleFamily } from './dqRules';
import { SEVERITY_TOKENS } from './dqTokens';

/* ────────────────────────────────────────────────────────────────────────────
 * Chains
 * ──────────────────────────────────────────────────────────────────────────── */

/** The short source token: "API" / "PDF". */
export function chainSource(chain: ValidationChain): string {
  return (chain.source || 'Source').toUpperCase();
}

/**
 * A stable id for a chain — its key, its `aria-controls` target and the value
 * carried in the URL.
 *
 * Keyed on source rather than array index so a refetch that reorders the chains
 * cannot swap the panel under a reader mid-sentence. `run_number` is in the key
 * only to stay unique if a company ever returns two chains of the same source.
 */
export function chainKey(chain: ValidationChain): string {
  return `${chainSource(chain)}-${chain.run_number ?? 0}`;
}

/** "PDF" → "Annual report (PDF)"; anything unrecognised is labelled as given. */
export function chainLabel(chain: ValidationChain): string {
  const source = chainSource(chain);
  if (source === 'PDF') return 'Annual report (PDF)';
  if (source === 'API') return 'Market data (API)';
  return chain.source || 'Source';
}

/**
 * The heading above a chain's report — the SOURCE, never "Chain 1 / Chain 2".
 *
 * A generic ordinal tells the reader nothing about which body of data they are
 * looking at, and the command bar's `scoring · run · document` line does not
 * answer it either. Shown even for a single-source company, so a reader can see
 * which one they have and infer that the other is possible.
 */
export function sectionHeading(chain: ValidationChain): string {
  const source = chainSource(chain);
  if (source === 'PDF') return 'PDF data validation';
  if (source === 'API') return 'Market data validation';
  return `${chain.source || 'Source'} validation`;
}

/** The one-line description of what the source actually is. */
export function chainDescription(chain: ValidationChain): string {
  const source = chainSource(chain);
  // Deliberately avoids the word "uploaded" — the provenance line right below
  // this (`pdfProvenance`) says "uploaded <date>" on its own, and the two
  // sentences reusing the same word read as redundant rather than confirming.
  if (source === 'PDF') return 'Figures extracted from the annual report you provided.';
  if (source === 'API') return 'Figures researched from the market data provider.';
  return 'Figures supplied by this source.';
}

/**
 * API chain first, PDF second; anything unrecognised keeps its arrival order
 * behind them. The researched chain is the baseline a reader compares an upload
 * against, so it reads first regardless of serialisation order.
 */
const SOURCE_ORDER: Record<string, number> = { API: 0, PDF: 1 };

export function orderChains(chains: ValidationChain[] | null | undefined): ValidationChain[] {
  return [...(chains ?? [])].sort(
    (a, b) => (SOURCE_ORDER[chainSource(a)] ?? 2) - (SOURCE_ORDER[chainSource(b)] ?? 2)
  );
}

/**
 * The chain to open on: the worst authoritative score.
 *
 * Defaulting to the first would put a clean API chain in front of a reader
 * whose PDF upload scored 0.72 — the problem hidden behind a click, which is
 * exactly what showing both sources was meant to prevent. Ties keep source
 * order, so two clean chains still open on API.
 *
 * Compares the score each chain calls authoritative, so a rule score is never
 * ranked against another chain's AI-adjusted one.
 */
export function worstChainIndex(chains: ValidationChain[]): number {
  let worst = 0;
  let worstScore = Number.POSITIVE_INFINITY;

  chains.forEach((chain, index) => {
    const { score } = resolveScore(chain.summary);
    if (!Number.isFinite(score)) return;
    if (score < worstScore) {
      worstScore = score;
      worst = index;
    }
  });

  return worst;
}

/**
 * The parts of a source control's label: source, year span, authoritative
 * score, and whether it is worth opening.
 *
 * Returned as parts rather than a joined string so the control can style the
 * score on its own — but the parts are what make the control worth having. A
 * bare "API / PDF" switch would hide the very thing a reader opened the console
 * for: that one source scores 0.72 while the other scores 0.91.
 */
export function tabLabelParts(chain: ValidationChain): {
  source: string;
  years: string;
  score: string;
  needsAttention: boolean;
} {
  const { summary } = chain;
  const { score } = resolveScore(summary);
  return {
    source: chainSource(chain),
    // From the periods the run actually checked, not from the issues: a clean
    // year still counts towards the span the source covers.
    years: formatPeriodRange((summary.periods ?? []).map((period) => period.year)),
    score: formatQualityScore(score),
    // Tied to `status`, so it agrees with the badge inside the panel rather
    // than inventing a second threshold of its own.
    needsAttention: summary.status !== 'clean',
  };
}

/**
 * "AR-2025.pdf · uploaded 12 Aug 2026" — the PDF chain's provenance.
 *
 * Returns null unless the backend actually supplied the fields: a document
 * whose name is unknown gets no line rather than a placeholder standing in for
 * one, which would name a document the run cannot vouch for.
 */
export function pdfProvenance(chain: ValidationChain): string | null {
  const parts: string[] = [];
  if (chain.document_name) parts.push(chain.document_name);
  if (chain.uploaded_at) {
    const when = new Date(chain.uploaded_at);
    if (!Number.isNaN(when.getTime())) {
      parts.push(
        `uploaded ${when.toLocaleDateString(undefined, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })}`
      );
    }
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Periods
 * ──────────────────────────────────────────────────────────────────────────── */

/** A year's worst severity — errors, else warnings, else clean. */
export function periodStatus(period: ValidationPeriod): ValidationStatus {
  if (period.errors > 0) return 'errors';
  if (period.warnings > 0) return 'warnings';
  return 'clean';
}

/** Per-year worst severity, for the dots on year selectors. */
export function severityByYear(
  periods: ValidationPeriod[] | null | undefined
): Record<number, ValidationStatus> {
  const map: Record<number, ValidationStatus> = {};
  for (const period of periods ?? []) {
    if (!period || !Number.isFinite(period.year)) continue;
    map[period.year] = periodStatus(period);
  }
  return map;
}

export interface PeriodProfileRow extends ValidationPeriod {
  /** "FY2024" — the categorical axis label. */
  label: string;
  status: ValidationStatus;
}

/**
 * The per-period chart rows, oldest → newest (the payload lists newest first).
 *
 * This is a profile ACROSS reporting periods within one run, not a history of
 * runs over time. The chart title must say so — there is no run history in this
 * system to chart.
 */
export function periodProfile(
  periods: ValidationPeriod[] | null | undefined
): PeriodProfileRow[] {
  return [...(periods ?? [])]
    .filter((p) => p && Number.isFinite(p.year))
    .sort((a, b) => a.year - b.year)
    .map((p) => ({ ...p, label: `FY${p.year}`, status: periodStatus(p) }));
}

/* ────────────────────────────────────────────────────────────────────────────
 * Statements
 * ──────────────────────────────────────────────────────────────────────────── */

export interface SupplyTotals {
  reported: number;
  derived: number;
  defaulted: number;
  total: number;
  /**
   * `total − (reported + derived + defaulted)`.
   *
   * The backend counts only three of its seven field classifications into those
   * buckets, so a period holding `estimated` / `absent` / `invalid` /
   * `reconstructed` values leaves a remainder the payload does not label. It is
   * reported as "other" rather than silently folded into one of the three.
   */
  unclassified: number;
  /** Statements this source supplied at all. */
  availableCount: number;
  statementCount: number;
}

export function supplyTotals(
  statements: ValidationStatement[] | null | undefined
): SupplyTotals {
  const rows = (statements ?? []).filter(Boolean);
  const available = rows.filter((r) => r.available);

  const reported = available.reduce((sum, r) => sum + (r.reported || 0), 0);
  const derived = available.reduce((sum, r) => sum + (r.derived || 0), 0);
  const defaulted = available.reduce((sum, r) => sum + (r.defaulted || 0), 0);
  const total = available.reduce((sum, r) => sum + (r.total || 0), 0);

  return {
    reported,
    derived,
    defaulted,
    total,
    unclassified: Math.max(0, total - reported - derived - defaulted),
    availableCount: available.length,
    statementCount: rows.length,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * Issues — facets and filtering
 * ──────────────────────────────────────────────────────────────────────────── */

export interface IssueFilter {
  severity: IssueSeverity[];
  family: RuleFamily[];
  years: number[];
  verdicts: string[];
}

export const EMPTY_FILTER: IssueFilter = {
  severity: [],
  family: [],
  years: [],
  verdicts: [],
};

export function isFilterActive(filter: IssueFilter): boolean {
  return (
    filter.severity.length > 0 ||
    filter.family.length > 0 ||
    filter.years.length > 0 ||
    filter.verdicts.length > 0
  );
}

type FacetKey = keyof IssueFilter;

/** Does one issue satisfy one facet? An empty facet selection matches all. */
function matchesFacet(issue: ValidationIssue, facet: FacetKey, filter: IssueFilter): boolean {
  switch (facet) {
    case 'severity':
      return filter.severity.length === 0 || filter.severity.includes(issue.severity);
    case 'family':
      return filter.family.length === 0 || filter.family.includes(ruleFamily(issue.code));
    case 'years':
      return (
        filter.years.length === 0 ||
        (issue.years ?? []).some((year) => filter.years.includes(year))
      );
    case 'verdicts':
      return (
        filter.verdicts.length === 0 ||
        (!!issue.ai_verdict && filter.verdicts.includes(issue.ai_verdict))
      );
  }
}

/** OR within a facet, AND across facets. */
export function filterIssues(
  issues: ValidationIssue[] | null | undefined,
  filter: IssueFilter
): ValidationIssue[] {
  const all = (issues ?? []).filter(Boolean);
  return all.filter((issue) =>
    (['severity', 'family', 'years', 'verdicts'] as FacetKey[]).every((facet) =>
      matchesFacet(issue, facet, filter)
    )
  );
}

export interface FacetOption<T> {
  value: T;
  label: string;
  count: number;
  selected: boolean;
}

export interface IssueFacets {
  severity: FacetOption<IssueSeverity>[];
  family: FacetOption<RuleFamily>[];
  years: FacetOption<number>[];
  verdicts: FacetOption<string>[];
}

/**
 * Counts for one facet are computed with that facet's OWN selection ignored.
 *
 * Otherwise selecting "errors" drops every warning chip to zero and the rail
 * becomes a dead end: the reader can no longer see that switching to warnings
 * would show them eleven more findings.
 */
function countsFor<T>(
  issues: ValidationIssue[],
  facet: FacetKey,
  filter: IssueFilter,
  valuesOf: (issue: ValidationIssue) => T[]
): Map<T, number> {
  const others = (['severity', 'family', 'years', 'verdicts'] as FacetKey[]).filter(
    (f) => f !== facet
  );
  const counts = new Map<T, number>();

  for (const issue of issues) {
    if (!others.every((f) => matchesFacet(issue, f, filter))) continue;
    for (const value of valuesOf(issue)) {
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
  }
  return counts;
}

export function issueFacets(
  issues: ValidationIssue[] | null | undefined,
  filter: IssueFilter,
  labelForVerdict: (verdict: string) => string
): IssueFacets {
  const all = (issues ?? []).filter(Boolean);

  const severityCounts = countsFor(all, 'severity', filter, (i) => [i.severity]);
  const familyCounts = countsFor(all, 'family', filter, (i) => [ruleFamily(i.code)]);
  const yearCounts = countsFor(all, 'years', filter, (i) => i.years ?? []);
  const verdictCounts = countsFor(all, 'verdicts', filter, (i) =>
    i.ai_verdict ? [i.ai_verdict] : []
  );

  // Every severity present ANYWHERE keeps a chip, even at a count of zero under
  // the current selection — a chip that disappears takes its own affordance
  // with it, so zero-count options are disabled rather than dropped.
  const presentSeverities = new Set(all.map((i) => i.severity));
  const presentFamilies = new Set(all.map((i) => ruleFamily(i.code)));
  const presentYears = new Set(all.flatMap((i) => i.years ?? []));
  const presentVerdicts = new Set(
    all.map((i) => i.ai_verdict).filter((v): v is string => !!v)
  );

  return {
    severity: (['error', 'warning', 'info'] as IssueSeverity[])
      .filter((s) => presentSeverities.has(s))
      .map((s) => ({
        value: s,
        label: SEVERITY_TOKENS[s].label,
        count: severityCounts.get(s) ?? 0,
        selected: filter.severity.includes(s),
      })),
    family: [...presentFamilies].map((f) => ({
      value: f,
      label: f,
      count: familyCounts.get(f) ?? 0,
      selected: filter.family.includes(f),
    })),
    years: [...presentYears]
      .sort((a, b) => b - a)
      .map((y) => ({
        value: y,
        label: `FY${y}`,
        count: yearCounts.get(y) ?? 0,
        selected: filter.years.includes(y),
      })),
    verdicts: [...presentVerdicts].map((v) => ({
      value: v,
      label: labelForVerdict(v),
      count: verdictCounts.get(v) ?? 0,
      selected: filter.verdicts.includes(v),
    })),
  };
}

/** Worst severity first, then the widest year span, then alphabetical. */
export function sortIssues(issues: ValidationIssue[]): ValidationIssue[] {
  return [...issues].sort((a, b) => {
    const bySeverity = SEVERITY_TOKENS[a.severity].rank - SEVERITY_TOKENS[b.severity].rank;
    if (bySeverity !== 0) return bySeverity;
    const bySpan = (b.years?.length ?? 0) - (a.years?.length ?? 0);
    if (bySpan !== 0) return bySpan;
    return a.label.localeCompare(b.label);
  });
}

/**
 * The orderings the findings table offers.
 *
 * Deliberately NOT including impact: `ValidationIssue` carries no impact field.
 * Cost is a per-FIELD figure that lives in the field-coverage payload, so a
 * findings table cannot rank by it without inventing numbers.
 */
export type IssueSort = 'severity' | 'years' | 'label' | 'verdict';

/** `severity` is `sortIssues` — the default every other view already uses. */
export function sortIssuesBy(issues: ValidationIssue[], sort: IssueSort): ValidationIssue[] {
  if (sort === 'severity') return sortIssues(issues);

  const copy = [...issues];

  switch (sort) {
    case 'years':
      // Widest span first — an issue touching nine years is a different problem
      // from one touching a single period.
      return copy.sort(
        (a, b) => (b.years?.length ?? 0) - (a.years?.length ?? 0) || a.label.localeCompare(b.label)
      );
    case 'verdict':
      // Reviewed issues first, so the reader can see what the agent looked at.
      // Unreviewed ones keep severity order behind them rather than being
      // shuffled by an empty string comparison.
      return copy.sort((a, b) => {
        const av = a.ai_verdict || '';
        const bv = b.ai_verdict || '';
        if (av && !bv) return -1;
        if (!av && bv) return 1;
        return av.localeCompare(bv) || SEVERITY_TOKENS[a.severity].rank - SEVERITY_TOKENS[b.severity].rank;
      });
    case 'label':
    default:
      return copy.sort((a, b) => a.label.localeCompare(b.label));
  }
}

/** Free-text match over an issue's human label and its rule code. */
export function issueMatchesQuery(issue: ValidationIssue, query: string): boolean {
  const needle = query.toLowerCase().replace(/[_\s-]+/g, ' ').trim();
  if (!needle) return true;
  const haystack = `${issue.label} ${issue.code}`.toLowerCase().replace(/[_\s-]+/g, ' ');
  return haystack.includes(needle);
}

/* ────────────────────────────────────────────────────────────────────────────
 * AI review
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * How many DISTINCT issues carry each verdict.
 *
 * Counts issues, not findings — the summary payload is already grouped by rule
 * code, so this can never claim more opinions than the reviewer gave.
 */
export function agentVerdictTally(
  issues: ValidationIssue[] | null | undefined
): Record<string, number> {
  const tally: Record<string, number> = {};
  for (const issue of issues ?? []) {
    if (!issue?.ai_verdict) continue;
    tally[issue.ai_verdict] = (tally[issue.ai_verdict] ?? 0) + 1;
  }
  return tally;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Source comparison
 * ──────────────────────────────────────────────────────────────────────────── */

export interface DivergenceRow {
  code: string;
  label: string;
  /** Per source key: the issue, or null when that source did not raise it. */
  bySource: Record<string, ValidationIssue | null>;
  /** True when exactly one source raised it — the rows worth reading first. */
  onlyOne: boolean;
}

/**
 * The union of codes across chains, one column per source.
 *
 * Deliberately produces NO combined score and no ranking. The chains validate
 * different bodies of data, so a code missing from one column means "this
 * source did not raise it", never "this source is better".
 */
export function divergence(chains: ValidationChain[]): DivergenceRow[] {
  const sources = chains.map(chainSource);
  const byCode = new Map<string, DivergenceRow>();

  chains.forEach((chain) => {
    const source = chainSource(chain);
    for (const issue of chain.summary.issues ?? []) {
      if (!issue?.code) continue;
      let row = byCode.get(issue.code);
      if (!row) {
        row = {
          code: issue.code,
          label: issue.label,
          bySource: Object.fromEntries(sources.map((s) => [s, null])),
          onlyOne: false,
        };
        byCode.set(issue.code, row);
      }
      row.bySource[source] = issue;
    }
  });

  const rows = [...byCode.values()];
  for (const row of rows) {
    row.onlyOne = sources.filter((s) => row.bySource[s]).length === 1;
  }

  // Divergent rows first — they are the reason this view exists — then by the
  // worst severity either source recorded.
  return rows.sort((a, b) => {
    if (a.onlyOne !== b.onlyOne) return a.onlyOne ? -1 : 1;
    const rank = (row: DivergenceRow) =>
      Math.min(
        ...sources.map((s) =>
          row.bySource[s] ? SEVERITY_TOKENS[row.bySource[s]!.severity].rank : 99
        )
      );
    const bySeverity = rank(a) - rank(b);
    return bySeverity !== 0 ? bySeverity : a.label.localeCompare(b.label);
  });
}

/* ────────────────────────────────────────────────────────────────────────────
 * Misc
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The smallest score movement worth calling a movement.
 *
 * Matches the backend's own threshold for promoting `quality_score_basis` to
 * `agent_adjusted`, and the epsilon `QualityRing` uses to decide whether to
 * draw its ghost arc — the two must agree or the ring and the composition bar
 * contradict each other about the same pair of numbers on the same screen.
 */
export const SCORE_EPSILON = 0.005;

/**
 * The AI adjustment as a signed delta.
 *
 * Three outcomes, deliberately distinguished:
 *   - `null`   — no adjustment governs this run. `quality_score_basis` is the
 *                authority here, never the mere presence of an adjusted
 *                number: the backend can compute one and still rule that the
 *                deterministic score stands.
 *   - `0`      — the reviewer looked and moved the score by less than the
 *                epsilon. "Looked and agreed" is a real result and reads
 *                differently from "never looked", so it is NOT collapsed into
 *                `null`; the caller says so in words.
 *   - non-zero — the signed movement, positive when the penalty was relieved.
 *
 * The zero case is reachable in production even though the backend gates the
 * basis on a raw delta above the epsilon: both scores are rounded to 2dp on the
 * way out, so 0.3751 → 0.3849 ships as 0.38 / 0.38. Returning the raw
 * difference there produced a signed "−0.00" and a claim that the penalty had
 * increased, which was the opposite of what happened.
 */
export function agentDelta(summary: ValidationSummary): number | null {
  const { isAgentAdjusted } = resolveScore(summary);
  const adjusted = summary.quality_score_agent_adjusted;
  if (!isAgentAdjusted || adjusted == null || !Number.isFinite(adjusted)) return null;
  if (!Number.isFinite(summary.quality_score)) return null;

  const delta = adjusted - summary.quality_score;
  return Math.abs(delta) < SCORE_EPSILON ? 0 : delta;
}
